/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * Everything the lease detail screen shows that the API does not hand over
 * ready-made.
 *
 * The lease read is deliberately thin -- `GET /agency/{id}/leases/{id}`
 * returns the stored document and nothing else -- so the monthly rent, the
 * competência situation and the activity feed all have to be derived here.
 * Keeping that arithmetic in pure functions is what makes it testable
 * without mounting the page.
 */

import type { ChargeLine, DueDatePolicy, LeaseClosure, LeaseExpectation, LeaseSuspensionPeriod } from '../types/lease.types';

/**
 * The charge-line kinds that carry the rent itself.
 *
 * Both spellings are real. The backend's activation guard and the
 * lease-from-won-deal factory write `"aluguel"`, but every lease actually
 * stored in the local database uses `"rent"` -- so a lookup for either one
 * alone finds the rent on some leases and misses it on others, showing
 * "aluguel não definido" over a lease that plainly has one. Accepting both
 * is the only reading that works against the data as it exists.
 */
export const RENT_CHARGE_KINDS: readonly string[] = ['aluguel', 'rent'];

/**
 * The states a competência can be in: the model's four, plus `cancelled`,
 * which the screen derives for the months a rescission wrote off (the
 * model stores those as `written_off`, the same as a baixa by hand).
 */
export type ExpectationState =
  | 'open'
  | 'awaiting_confirmation'
  | 'settled'
  | 'written_off'
  | 'cancelled';

/**
 * How one competência row should read to a broker.
 *
 * `awaiting` outranks `overdue` on purpose: a row the system has not
 * confirmed yet is not the tenant's fault, so calling it late would
 * accuse the wrong party.
 */
export type SituationTone = 'settled' | 'awaiting' | 'overdue' | 'open' | 'writtenOff' | 'cancelled';

export interface Situation {
  tone: SituationTone;
  /** Days late -- only meaningful when `tone` is `overdue`. */
  daysOverdue: number;
}

/**
 * Formats an integer cents amount as BRL currency (`12345` -> "R$ 123,45").
 *
 * @param cents - The amount in cents.
 * @returns The formatted currency string.
 */
export function formatCents(cents: number): string {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' })
    .format(cents / 100);
}

/**
 * Renders an ISO date the way a Brazilian broker reads it: `DD/MM/YYYY`.
 *
 * The API sends plain `YYYY-MM-DD` (and, for timestamps, an ISO string
 * whose date half is all this screen shows). Parsing is deliberately
 * textual rather than through `Date`: `new Date('2026-01-21')` is UTC
 * midnight, which renders as the 20th for every user west of Greenwich.
 *
 * @param value - The API value, or an empty string.
 * @returns The formatted date, or the input unchanged when it is not one.
 */
export function formatIsoDate(value: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  return match ? `${match[3]}/${match[2]}/${match[1]}` : value;
}

/**
 * Renders a `YYYY-MM` accrual period as a named month: `jan/2026`.
 *
 * Named rather than numeric because the column reads as a date either
 * way, and `01/2026` invites the reader to wonder whether the first part
 * is a day. The ISO string stays the sort key, so chronological order is
 * unaffected by how it is displayed.
 *
 * @param accrualPeriod - The period as the API stores it.
 * @param locale - BCP-47 tag; defaults to the app's Portuguese.
 * @returns The display form, or the input unchanged when it is not a period.
 */
export function formatAccrualPeriod(accrualPeriod: string, locale = 'pt-BR'): string {
  const match = /^(\d{4})-(\d{2})$/.exec(accrualPeriod);
  if (!match) return accrualPeriod;

  const [, year, month] = match;
  // Day 1 in UTC, read back in UTC: a local-time Date would slip to the
  // previous month for anyone west of Greenwich, naming December for a
  // January period.
  const monthName = new Intl.DateTimeFormat(locale, { month: 'short', timeZone: 'UTC' })
    .format(new Date(Date.UTC(Number(year), Number(month) - 1, 1)))
    .replace('.', '');
  return `${monthName}/${year}`;
}

/**
 * The monthly rent, in cents, from the lease's charge schedule.
 *
 * Only the fixed-amount rent line can be stated as a number: a line billed
 * as a percentage of the rent has no absolute value on its own, so it
 * yields null rather than a misleading zero.
 *
 * @param chargeSchedule - The lease's charge lines.
 * @returns The rent in cents, or null when it is not a fixed amount.
 */
export function findMonthlyRentCents(chargeSchedule: ChargeLine[]): number | null {
  const rent = chargeSchedule.find((line) => RENT_CHARGE_KINDS.includes(line.kind));
  return rent?.amount_cents ?? null;
}

/**
 * Marks the months a rescission cancelled as `cancelled`.
 *
 * The backend writes those months off, the same state a broker's own
 * baixa leaves, so the table called them "Baixa sem pagamento" as if
 * someone had forgiven the rent. The lease's closure names them by id.
 *
 * @param expectations - The competência panel.
 * @param cancelledIds - `closure.cancelled_expectation_ids` of the lease.
 * @returns The panel with those months' state replaced.
 */
export function markRescissionCancellations(
  expectations: LeaseExpectation[],
  cancelledIds: readonly string[] | undefined,
): LeaseExpectation[] {
  if (!cancelledIds?.length) return expectations;
  const cancelled = new Set(cancelledIds);
  return expectations.map((e) => (e.id && cancelled.has(e.id) ? { ...e, state: 'cancelled' } : e));
}

/**
 * Classifies one competência into the situation the table renders.
 *
 * @param expectation - The competência row.
 * @returns Its tone and, when late, the day count.
 */
export function resolveSituation(expectation: LeaseExpectation): Situation {
  const byState: Partial<Record<string, SituationTone>> = {
    settled: 'settled',
    written_off: 'writtenOff',
    cancelled: 'cancelled',
    awaiting_confirmation: 'awaiting',
  };
  const tone = byState[expectation.state];
  if (tone) {
    return { tone, daysOverdue: 0 };
  }
  return expectation.is_overdue
    ? { tone: 'overdue', daysOverdue: expectation.days_overdue }
    : { tone: 'open', daysOverdue: 0 };
}

/**
 * The accrual period covering a given day, in the API's `YYYY-MM` form.
 *
 * @param today - The reference date.
 * @returns The period string.
 */
export function accrualPeriodOf(today: Date): string {
  const month = String(today.getMonth() + 1).padStart(2, '0');
  return `${today.getFullYear()}-${month}`;
}

/**
 * The accrual period immediately before the given one.
 *
 * @param accrualPeriod - A `YYYY-MM` period.
 * @returns The preceding period, or the input when it cannot be parsed.
 */
export function previousAccrualPeriod(accrualPeriod: string): string {
  const match = /^(\d{4})-(\d{2})$/.exec(accrualPeriod);
  if (!match) return accrualPeriod;
  const year = Number(match[1]);
  const month = Number(match[2]);
  return month === 1
    ? `${year - 1}-12`
    : `${year}-${String(month - 1).padStart(2, '0')}`;
}

/**
 * Competências newest first, which is the order the panel reads in.
 *
 * `YYYY-MM` sorts correctly as plain text, so no date parsing is needed.
 *
 * @param expectations - The rows as the API returned them.
 * @returns A new array, newest period first.
 */
export function sortExpectationsDesc(expectations: LeaseExpectation[]): LeaseExpectation[] {
  return [...expectations].sort((a, b) => b.accrual_period.localeCompare(a.accrual_period));
}

/**
 * Finds one competência by period.
 *
 * @param expectations - The rows to search.
 * @param accrualPeriod - The `YYYY-MM` period wanted.
 * @returns The row, or null when the period has not been generated.
 */
export function findExpectation(
  expectations: LeaseExpectation[],
  accrualPeriod: string,
): LeaseExpectation | null {
  return expectations.find((e) => e.accrual_period === accrualPeriod) ?? null;
}

/**
 * What is still owed across every competência, in cents.
 *
 * Rows awaiting confirmation are left out: the panel's own footnote
 * promises they do not count until confirmed, and counting them would
 * bill the tenant for a month the system has not agreed on yet.
 *
 * @param expectations - The competência rows.
 * @returns The open balance in cents.
 */
export function totalOpenCents(expectations: LeaseExpectation[]): number {
  return expectations
    .filter((e) => e.state === 'open')
    .reduce((sum, e) => sum + e.balance_cents, 0);
}

/**
 * What has actually come in across every competência, in cents.
 *
 * @param expectations - The competência rows.
 * @returns The received total in cents.
 */
export function totalReceivedCents(expectations: LeaseExpectation[]): number {
  return expectations.reduce((sum, e) => sum + e.received_cents, 0);
}

/**
 * The day of the month competências fall due, when the lease says.
 *
 * @param policy - The lease's due-date policy, if it sent one.
 * @returns The day, or null when the policy does not state one.
 */
export function dueDayOfMonth(policy: DueDatePolicy | undefined): number | null {
  return policy?.day_of_month ?? policy?.payment_day ?? null;
}

/** One entry in the "Atividade da locação" column. */
export interface ActivityEntry {
  id: string;
  /** i18n key under `detail.activity.entries`. */
  titleKey: string;
  /** Interpolation values for the title's detail line. */
  detail: string;
  /** ISO-ish date the event happened, or empty when unknown. */
  when: string;
  /** Whether the system produced the entry rather than a person. */
  automatic: boolean;
}

export interface ActivitySources {
  activatedAt: string;
  originDealId: string;
  expectations: LeaseExpectation[];
  documentCount: number;
  /** How the contract ended, when it has. */
  closure?: LeaseClosure | null;
  suspensionPeriods?: LeaseSuspensionPeriod[];
}

/**
 * The ways a contract ends, newest first: closed after rescinded, and a
 * cancelled draft on its own.
 *
 * @param closure - The lease's closure, if any.
 * @returns One entry per end the closure records.
 */
function closureEntries(closure: LeaseClosure | null | undefined): ActivityEntry[] {
  const ends: Array<[string, string | null | undefined, string]> = [
    ['closed', closure?.closed_at, ''],
    ['rescinded', closure?.rescinded_at, ''],
    ['cancelled', closure?.cancelled_at, closure?.cancellation_reason ?? ''],
  ];
  return ends
    .filter(([, when]) => Boolean(when))
    .map(([titleKey, when, detail]) => ({
      id: titleKey,
      titleKey,
      detail,
      when: formatIsoDate((when as string).slice(0, 10)),
      automatic: false,
    }));
}

/**
 * Each suspension and, once it ended, its resumption; newest first.
 *
 * @param periods - The lease's suspension periods.
 * @returns The entries those periods prove.
 */
function suspensionEntries(periods: LeaseSuspensionPeriod[] | undefined): ActivityEntry[] {
  return [...(periods ?? [])]
    .sort((a, b) => b.from_date.localeCompare(a.from_date))
    .flatMap((period) => {
      const suspended: ActivityEntry = {
        id: `suspended-${period.from_date}`,
        titleKey: 'suspended',
        detail: period.reason ?? '',
        when: formatIsoDate(period.from_date),
        automatic: false,
      };
      if (!period.to_date) return [suspended];
      const resumed: ActivityEntry = {
        id: `resumed-${period.to_date}`,
        titleKey: 'resumed',
        detail: '',
        when: formatIsoDate(period.to_date),
        automatic: false,
      };
      return [resumed, suspended];
    });
}

/**
 * Builds the activity column from the reads the page already performs.
 *
 * There is no lease-events endpoint -- the backend stores no audit trail
 * for a lease -- so rather than leave the column empty (or invent a feed),
 * this reconstructs the events that the data itself proves happened: the
 * lease was activated, competências were settled, papers were attached,
 * it was suspended, rescinded, closed or cancelled. Anything the API does
 * not evidence simply does not appear.
 *
 * @param sources - The already-loaded lease facts.
 * @returns Activity entries, most recent first.
 */
export function buildActivity(sources: ActivitySources): ActivityEntry[] {
  // Newest first, whatever order the panel arrived in: the page hands its
  // rows over oldest first, which put February right under a rescission.
  const settlements = sources.expectations
    .filter((e) => e.state === 'settled' && e.settled_on)
    .sort((a, b) => (b.settled_on ?? '').localeCompare(a.settled_on ?? ''))
    .map<ActivityEntry>((e) => ({
      id: `settled-${e.accrual_period}`,
      titleKey: 'settled',
      detail: `${formatCents(e.received_cents)} · ${formatAccrualPeriod(e.accrual_period)}`,
      when: formatIsoDate(e.settled_on ?? ''),
      automatic: false,
    }));

  const entries: ActivityEntry[] = [
    ...closureEntries(sources.closure),
    ...suspensionEntries(sources.suspensionPeriods),
    ...settlements,
  ];

  if (sources.documentCount > 0) {
    entries.push({
      id: 'documents',
      titleKey: 'documents',
      detail: String(sources.documentCount),
      when: '',
      automatic: false,
    });
  }

  if (sources.activatedAt) {
    entries.push({
      id: 'activated',
      titleKey: 'activated',
      detail: '',
      when: formatIsoDate(sources.activatedAt),
      automatic: true,
    });
  }

  if (sources.originDealId) {
    entries.push({
      id: 'created',
      titleKey: 'created',
      detail: '',
      when: '',
      automatic: true,
    });
  }

  return entries;
}

/** The two competências the detail screen leads with. */
export interface CurrentCompetences {
  /** The period covering today, `YYYY-MM`. */
  currentPeriod: string;
  current: LeaseExpectation | null;
  /** The month before it, `YYYY-MM`. */
  previousPeriod: string;
  previous: LeaseExpectation | null;
}

/**
 * The current and previous competências for a lease.
 *
 * Composed at the call site until the detail header needed the same pair the
 * KPI strip already had. Two independent compositions of "which month are we
 * in" is one too many, so it lives here now.
 *
 * A period the generator has not reached yet resolves to `null` rather than
 * to an empty expectation — an absent competência is not a settled one, and
 * the callers must be able to tell those apart.
 *
 * @param expectations - Every competência the lease has.
 * @param today - The reference date, injected so callers stay testable.
 * @returns The two periods and whichever expectations exist for them.
 */
export function currentCompetences(
  expectations: LeaseExpectation[],
  today: Date,
): CurrentCompetences {
  const currentPeriod = accrualPeriodOf(today);
  const previousPeriod = previousAccrualPeriod(currentPeriod);
  return {
    currentPeriod,
    current: findExpectation(expectations, currentPeriod),
    previousPeriod,
    previous: findExpectation(expectations, previousPeriod),
  };
}
